import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Volume2, VolumeX, Heart, Lamp, ArrowRight, Sparkles } from "lucide-react";
import { getLantern } from "@/lib/api";
import { sceneById } from "@/lib/scenes";
import { SanctuaryEngine } from "@/lib/audioEngine";

export default function Lantern() {
  const { code } = useParams();
  const [note, setNote] = useState(null);
  const [status, setStatus] = useState("loading");
  const [broken, setBroken] = useState(false);
  const [playing, setPlaying] = useState(false);
  const engineRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    getLantern(code)
      .then((d) => { if (mounted) { setNote(d); setStatus("ok"); } })
      .catch(() => { if (mounted) setStatus("error"); });
    engineRef.current = new SanctuaryEngine();
    return () => { mounted = false; engineRef.current?.stop(); };
  }, [code]);

  const toggleSound = async () => {
    const eng = engineRef.current;
    if (!eng || !note) return;
    if (playing) { eng.stop(); setPlaying(false); return; }
    await eng.play(note.scene);
    eng.setVolume(0.55);
    setPlaying(true);
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-bsn-midnight flex items-center justify-center" data-testid="lantern-loading">
        <Loader2 className="w-8 h-8 text-bsn-gold animate-spin" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-bsn-midnight flex flex-col items-center justify-center text-center px-6" data-testid="lantern-error">
        <Lamp className="w-12 h-12 text-bsn-gold/70 mb-5" />
        <h1 className="font-serif text-3xl text-bsn-champagne">This lantern couldn't be found</h1>
        <p className="text-bsn-ice/70 mt-3 max-w-md">The link may be incomplete or expired — but a calm moment is always here for you.</p>
        <Link to="/sanctuary" className="mt-7 inline-flex items-center gap-2 btn-gold font-semibold px-6 py-3 rounded-full">
          Enter the Sanctuary <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const scene = sceneById(note.scene);
  const greeting = note.recipient_name ? `${note.recipient_name},` : "For you,";

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-bsn-midnight" data-testid="lantern-page">
      {/* Scene background */}
      {broken ? (
        <img src={`/sanctuary_videos/${note.scene}.jpg`} alt="" className="absolute inset-0 w-full h-full object-cover kenburns" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <video
          key={note.scene}
          src={`/sanctuary_videos/${note.scene}.mp4`}
          poster={`/sanctuary_videos/${note.scene}.jpg`}
          autoPlay loop muted playsInline
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setBroken(true)}
          data-testid="lantern-scene-video"
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-b from-bsn-midnight/80 via-bsn-midnight/55 to-bsn-midnight/90" />

      {/* Sound toggle */}
      <button onClick={toggleSound} className="absolute top-5 right-5 z-20 inline-flex items-center gap-2 bg-bsn-midnight/60 backdrop-blur-sm border border-bsn-gold/30 text-bsn-champagne px-4 py-2.5 rounded-full text-sm font-semibold hover:bg-bsn-midnight/80 transition-colors" data-testid="lantern-sound-toggle">
        {playing ? <><VolumeX className="w-4 h-4" /> Mute {scene?.name}</> : <><Volume2 className="w-4 h-4" /> Play {scene?.name}</>}
      </button>

      {/* Message */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-5 py-20">
        <div className="max-w-xl w-full text-center">
          <div className="w-16 h-16 rounded-full bg-bsn-gold/20 border border-bsn-gold/40 flex items-center justify-center mx-auto mb-7 animate-pulse">
            <Lamp className="w-8 h-8 text-bsn-gold" />
          </div>
          <p className="text-bsn-gold font-mono uppercase tracking-[0.2em] text-xs mb-4">A Lantern, Lit for You</p>
          <h1 className="font-serif text-3xl sm:text-4xl text-bsn-champagne leading-tight mb-6" data-testid="lantern-greeting">{greeting}</h1>
          <p className="text-bsn-ice/85 text-lg leading-relaxed">
            <span className="font-semibold text-bsn-champagne">{note.sender_name}</span> was thinking of you, and wanted you to have a moment of calm.
          </p>

          <blockquote className="mt-8 bg-bsn-navy/50 backdrop-blur-sm border border-bsn-gold/25 rounded-2xl p-6 sm:p-8" data-testid="lantern-message-text">
            <Sparkles className="w-6 h-6 text-bsn-gold/70 mx-auto mb-3" />
            <p className="font-serif text-xl sm:text-2xl text-bsn-parchment leading-relaxed whitespace-pre-line">“{note.message}”</p>
          </blockquote>

          <p className="text-bsn-ice/70 text-sm mt-8 leading-relaxed max-w-md mx-auto">
            Stay a while. Breathe slowly. If today feels heavy, you're not alone — you can call or text
            <strong className="text-bsn-champagne"> 988</strong> anytime, day or night.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/sanctuary" className="inline-flex items-center gap-2 btn-gold font-semibold px-6 py-3 rounded-full" data-testid="lantern-enter-sanctuary">
              Enter the Sanctuary <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/sanctuary#lantern" className="inline-flex items-center gap-2 border border-bsn-gold/40 text-bsn-champagne hover:bg-bsn-gold/10 font-semibold px-6 py-3 rounded-full transition-colors" data-testid="lantern-send-back">
              <Heart className="w-4 h-4" /> Light one for someone
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
